import type { QueryClient } from "@tanstack/react-query";
import { knownParticipation, noteParticipation } from "./answerSession";
import { forgetDraft } from "./draft";

/**
 * ═══ UNE PARTICIPATION SUPPRIMÉE PAR LE BACK-OFFICE NE LAISSE RIEN DERRIÈRE ELLE ═══
 * (participation supprimée depuis la fiche contact, 2026-10-20)
 *
 * Le bouton « Désactiver » de la fiche contact (`bo/src/pages/user/Event/Remove.jsx`) envoie
 * `DELETE apix /domains/:d/events/:e/users/:u` : la ligne `UserEvents` est EFFACÉE
 * (`api/src/routes/participations/remove`), et la lecture de la FO rend ensuite
 * `UserEvent: null`. Rien, côté navigateur, n'en savait rien : l'état de navigation gardait
 * les réponses et les étapes franchies, le cache gardait l'inscription, et la confirmation
 * rouverte par un retour arrière réécrivait tout.
 *
 * La SEULE preuve est la réponse du serveur : une participation connue (un identifiant) qui
 * revient absente, ou sous un autre identifiant. On la compare à celle vue en dernier, en
 * mémoire pour cette page, et dans `localStorage` pour un rechargement, un nouvel onglet ou
 * le lien d'invitation (`gk-participation:<domaine>:<événement>:<contact>`). Alors tout ce
 * que ce navigateur gardait de CETTE participation (cet événement, ce contact) est vidé :
 *
 *   - les réponses du tunnel dans l'historique : écartées à la lecture par leur empreinte
 *     (`answerSession.ts`), toutes les entrées, y compris celles du bouton Retour ;
 *   - le brouillon d'un premier parcours (`draft.ts`) ;
 *   - les marques « email déjà envoyé » de l'onglet (`Confirmation.tsx`, `Checkout.tsx`,
 *     `emails/send.ts`) ;
 *   - le cache des requêtes de cet événement et de ce contact (commande, solde, conférences,
 *     factures, formulaires, accompagnants…), remis à zéro et relu.
 */
export type ParticipationScope = {
  domainId: number;
  eventId: number;
  userId: number;
  session: string;
};

const markerKey = ({ domainId, eventId, userId }: ParticipationScope) =>
  `gk-participation:${domainId}:${eventId}:${userId}`;

function lastSeen(scope: ParticipationScope): number | null | undefined {
  const inPage = knownParticipation(scope.session);
  if (inPage !== undefined) return inPage;
  try {
    const raw = globalThis.localStorage.getItem(markerKey(scope));
    const id = raw === null ? NaN : Number(raw);
    return Number.isFinite(id) ? id : undefined;
  } catch {
    return undefined;
  }
}

function remember(scope: ParticipationScope, id: number | null): void {
  noteParticipation(scope.session, id);
  try {
    if (id === null) globalThis.localStorage.removeItem(markerKey(scope));
    else globalThis.localStorage.setItem(markerKey(scope), String(id));
  } catch {
    /* stockage indisponible : la mémoire de la page suffit pour cette visite */
  }
}

/** Les marques d'email de cet onglet qui parlent de cette participation. */
export function participationMarks(keys: ReadonlyArray<string>, scope: ParticipationScope): string[] {
  const { eventId, userId } = scope;
  return keys.filter(
    (key) =>
      key.startsWith(`confirmation-emailed:${eventId}:`) ||
      key.startsWith(`paid-emailed:${eventId}:${userId}:`) ||
      key.startsWith(`emailed:${eventId}:`),
  );
}

/** Tout ce que ce navigateur gardait de cette participation, vidé. */
export function purgeParticipation(client: QueryClient, scope: ParticipationScope): void {
  const { domainId, eventId, userId } = scope;
  forgetDraft(domainId, eventId, userId);
  try {
    const store = globalThis.sessionStorage;
    const keys = Array.from({ length: store.length }, (_, i) => store.key(i) ?? "");
    for (const key of participationMarks(keys, scope)) store.removeItem(key);
  } catch {
    /* stockage indisponible : aucune marque à effacer */
  }
  /* La participation elle-même n'en fait pas partie : c'est sa propre lecture qui parle. */
  void client
    .resetQueries({
      predicate: ({ queryKey: key }) =>
        key[0] !== "participation" &&
        key[1] === domainId &&
        key[2] === eventId &&
        key[3] === userId,
    })
    .catch(() => undefined);
}

/**
 * Ce que le serveur vient de LIRE pour cette participation (`null` : aucune). Une participation
 * connue qui a disparu ou changé d'identifiant fait tout vider.
 */
export function participationRead(
  client: QueryClient,
  scope: ParticipationScope,
  id: number | null,
): void {
  const before = lastSeen(scope);
  if (typeof before === "number" && before !== id) purgeParticipation(client, scope);
  remember(scope, id);
}

/** Ce que l'écriture vient de rendre : c'est la participation, sans rien à comparer. */
export function participationWritten(scope: ParticipationScope, id: number | null): void {
  remember(scope, id);
}
